<template>
    <div class="modal fade show" aria-labelledby="addNewCardTitle" style="display: block; background: #1c1d1c33; z-index: 9999;">
        <div class="modal-dialog modal-dialog-centered" ref="modalRef">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                            <h1 class="mb-0">{{ (isEdit ? 'Edit' : 'Add') + ' API Receiver' }}</h1>
                            <button
                                @click="onClickClose"
                                type="button"
                                class="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                </div>

                <hr class="mt-0 mb-0">

                <div class="pb-2">
                    <div class="col-11 py-1 mx-auto">
                        
                        <div class="mb-1">
                            <label class="form-label"> Contact Profile</label>
                            <AsyncSelect
                                placeholder="Select Contact Profile"
                                :api-service="fetchContactProfileDropdownList"
                                v-model="formData.contact_profile_id"
                                :additional-query="{ company_id: route.params.companyId }"
                                label="name"
                            />
                            <transition name="fade">
                                <span v-if="formError.contact_profile_id" class="invalid-feedback d-block qcont">{{ formError.contact_profile_id }}</span>
                            </transition>
                        </div>

                        <div class="mb-1">
                            <label class="form-label"> <span class="required">*</span> Endpoint (Base URL)</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="formData.endpoint"
                                placeholder="https://parent-server.com"
                            />
                            <transition name="fade">
                                <span v-if="formError.endpoint" class="invalid-feedback d-block qcont">{{ formError.endpoint }}</span>
                            </transition>
                        </div>

                        <div class="mb-1">
                            <label class="form-label"> <span class="required">*</span> Authorized Services</label>
                            <v-select
                                multiple
                                placeholder="Select Services"
                                :options="servicesList"
                                v-model="formData.services"
                                :reduce="service => service.value"
                                label="label"
                            />
                            <transition name="fade">
                                <span v-if="formError.services" class="invalid-feedback d-block qcont">{{ formError.services }}</span>
                            </transition>
                        </div>

                        <div class="mb-1" v-if="!isEdit">
                            <label class="form-label"> <span class="required">*</span> API Key</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="formData.api_key"
                                placeholder="Enter API Key from Parent Server"
                            />
                            <transition name="fade">
                                <span v-if="formError.api_key" class="invalid-feedback d-block qcont">{{ formError.api_key }}</span>
                            </transition>
                        </div>

                        <div v-if="isEdit" class="mb-1">
                            <label class="form-label"> <span class="required">*</span> Status</label>
                            <v-select
                                placeholder="Select Status"
                                v-model="formData.status"
                                :options="statusOptions"
                                :reduce="option => option.value"
                                label="label"
                            />
                        </div>

                        <div class="d-flex justify-content-end mt-3">
                            <button @click="onClickClose" type="button" class="btn btn-outline-secondary me-1">Cancel</button>
                            <button @click="onSubmit" type="button" class="btn btn-primary" :disabled="loading">
                                <span v-if="loading" class="spinner-border spinner-border-sm me-50" role="status" aria-hidden="true"></span>
                                {{ isEdit ? 'Update' : 'Save' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { reactive, ref, inject } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import handleApiIntegrationHub from '@/services/modules/apiIntegrationHub';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import handleDropdowns from '@/services/modules/dropdowns';

const props = defineProps({
    isEdit: Boolean,
    formData: Object,
    editData: Object,
    servicesList: Array
});

const emit = defineEmits(['onSaved']);

const store = useStore();
const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false);

const { storeApiReceiver, updateApiReceiver } = handleApiIntegrationHub();
const { fetchContactProfileDropdownList } = handleDropdowns();


const statusOptions = ref([
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
]);

const formError = reactive({
    endpoint: '',
    api_key: '',
    contact_profile_id: '',
    services: ''
});

const checkValidation = () => {
    let flag = false;
    formError.endpoint = '';
    formError.api_key = '';
    formError.contact_profile_id = '';
    formError.services = '';

    if (!props.formData.endpoint) {
        formError.endpoint = 'Endpoint is required';
        flag = true;
    }
    if (!props.isEdit && !props.formData.api_key) {
        formError.api_key = 'API Key is required';
        flag = true;
    }

    if (!props.formData.services || props.formData.services.length === 0) {
        formError.services = 'Select at least one service';
        flag = true;
    }

    return flag;
}

const getQuery = () => {
    return `?company_id=${route.params.companyId}`;
}

const handleUpdate = async (query) => {
    const res = await updateApiReceiver(props.formData.id, {
        status: props.formData.status,
        services: props.formData.services,
        endpoint: props.formData.endpoint ? props.formData.endpoint.trim() : '',
        api_key: props.formData.api_key ? props.formData.api_key.trim() : '',
        contact_profile_id: props.formData.contact_profile_id && typeof props.formData.contact_profile_id === 'object' ? props.formData.contact_profile_id.id : props.formData.contact_profile_id,
        type: 'remote'
    }, query);

    if (!res.status) {
        showError(res.message || 'Failed to update');
        return false;
    }

    showSuccess(res.message || 'Updated successfully');
    emit('onSaved');
    return true;
}

const handleStore = async (query) => {
    const payload = {
        endpoint: props.formData.endpoint ? props.formData.endpoint.trim() : '',
        api_key: props.formData.api_key ? props.formData.api_key.trim() : '',
        services: props.formData.services,
        contact_profile_id: props.formData.contact_profile_id && typeof props.formData.contact_profile_id === 'object' ? props.formData.contact_profile_id.id : props.formData.contact_profile_id,
        type: 'remote'
    };
    const res = await storeApiReceiver(payload, query);

    if (!res.status) {
        showError(res.message || 'Failed to save');
        return false;
    }

    showSuccess(res.message || 'Saved successfully');
    emit('onSaved');
    return true;
}

const getErrorMessage = (err) => {
    const hasMessage = err && err.response && err.response.data && err.response.data.message;
    return hasMessage ? err.response.data.message : 'Something went wrong while saving. Please try again.';
}

const onSubmit = async () => {
    if (checkValidation()) return;
    loading.value = true;

    try {
        const query = getQuery();
        let success = false;

        if (props.isEdit) {
            success = await handleUpdate(query);
        }
        
        if (!props.isEdit) {
            success = await handleStore(query);
        }

        if (success) {
            onClickClose();
        }
    } catch (err) {
        showError(getErrorMessage(err));
    } finally {
        loading.value = false;
    }
}

const onClickClose = () => {
    store.state.isModalOpenTwo = false;
}
</script>

<style scoped>
.required {
    color: red;
}
</style>
